import { instrumentAsyncIterable } from "../instrumentation/index.js";
import { collectAsyncIterableToSet, firstOrThrow, OrderedQueue } from "../support/index.js";
/**
 * Traverses DAG node IDs by eagerly collecting every ID reachable from `excludeNodeId` before
 * walking from the include-side starting ID. The yielded set is
 * `reachable(nodeId) - reachable(excludeNodeId)`.
 */
export async function* walkDagNodeIdsEagerExclude(context, nodeId, excludeNodeId, options = {}) {
    yield* instrumentAsyncIterable(context.instrumentation, "dag.traversal", (span) => walkDagNodeIdsEagerExcludeCore({
        ...context,
        role: "main",
        telemetry: { span, countYieldedNodes: true },
    }, nodeId, excludeNodeId, options), { attributes: { strategy: "eagerExclude" } });
}
async function* walkDagNodeIdsEagerExcludeCore(context, nodeId, excludeNodeId, options) {
    const excluded = excludeNodeId !== undefined
        ? await collectAsyncIterableToSet(walkDagReachableNodeIdsCore({
            ...context,
            role: "exclude",
            telemetry: { ...context.telemetry, countYieldedNodes: false },
        }, [excludeNodeId], options))
        : new Set();
    if (excludeNodeId !== undefined) {
        context.telemetry.span.incrementCounter("excluded_nodes", excluded.size);
    }
    const reachable = new Set();
    const factory = createDagFrontierItemFactory(createBasicDagSchedulingContext);
    const frontier = options.createFrontier?.() ??
        createDefaultTraversalFrontier();
    frontier.enqueue(factory.createStartItem(nodeId, "main"));
    while (!frontier.isEmpty()) {
        const item = frontier.dequeueOrThrow();
        context.telemetry.span.incrementCounter("traversal_steps");
        if (reachable.has(item.nodeId) || excluded.has(item.nodeId)) {
            context.telemetry.span.incrementCounter("stale_steps");
            continue;
        }
        reachable.add(item.nodeId);
        recordYieldedNode(context);
        yield item.nodeId;
        const successors = await expandDagSuccessors(context, item.nodeId);
        const successorItems = factory
            .createSuccessorItems(item, successors)
            .filter((successor) => !reachable.has(successor.nodeId) && !excluded.has(successor.nodeId));
        frontier.enqueueMany(successorItems);
    }
}
/**
 * Traverses node IDs with a lazy two-sided view of the DAG. It buffers include-side candidates,
 * marks exclude-side stop points as they are encountered, and yields only when a conservative path
 * certificate proves that older exclude successors cannot affect the result set. If that
 * certificate is not available, it falls back to a full exclude-reachable collection.
 */
export async function* walkDagNodeIdsCertifiedLazy(context, nodeId, excludeNodeId, options = {}) {
    yield* instrumentAsyncIterable(context.instrumentation, "dag.traversal", (span) => walkDagNodeIdsCertifiedLazyCore({
        ...context,
        role: "main",
        telemetry: { span, countYieldedNodes: true },
    }, nodeId, excludeNodeId, options), { attributes: { strategy: "certifiedLazy" } });
}
async function* walkDagNodeIdsCertifiedLazyCore(context, nodeId, excludeNodeId, options) {
    if (excludeNodeId === undefined) {
        yield* walkDagNodeIdsEagerExcludeCore(context, nodeId, undefined, options);
        return;
    }
    const states = new Map();
    const resultCandidates = new Set();
    const stopPoints = new Set();
    const includeExpanded = new Set();
    let includePathReachedTerminal = false;
    let singleExcludeSuccessor = null;
    let excludePathSplit = false;
    const stateFor = (nodeId) => {
        let state = states.get(nodeId);
        if (state === undefined) {
            state = { fromInclude: false, fromExclude: false };
            states.set(nodeId, state);
        }
        return state;
    };
    const markExcludeReached = (excludeReachedNodeId) => {
        stateFor(excludeReachedNodeId).fromExclude = true;
    };
    markExcludeReached(excludeNodeId);
    const excludeStartSuccessors = await expandDagSuccessors({ ...context, role: "exclude" }, excludeNodeId);
    for (const successor of excludeStartSuccessors)
        markExcludeReached(successor.nodeId);
    if (hasPathSplit(excludeStartSuccessors))
        excludePathSplit = true;
    singleExcludeSuccessor =
        excludeStartSuccessors.length === 1 ? firstOrThrow(excludeStartSuccessors).nodeId : null;
    const factory = createDagFrontierItemFactory(createBasicDagSchedulingContext);
    const includeFrontier = options.createFrontier?.() ??
        createDefaultTraversalFrontier();
    includeFrontier.enqueue(factory.createStartItem(nodeId, "main"));
    while (!includeFrontier.isEmpty()) {
        const item = includeFrontier.dequeueOrThrow();
        context.telemetry.span.incrementCounter("traversal_steps");
        const state = stateFor(item.nodeId);
        if (includeExpanded.has(item.nodeId)) {
            context.telemetry.span.incrementCounter("stale_steps");
            continue;
        }
        if (state.fromExclude) {
            stopPoints.add(item.nodeId);
            await markExcludeSuccessors(item.nodeId);
            continue;
        }
        state.fromInclude = true;
        includeExpanded.add(item.nodeId);
        resultCandidates.add(item.nodeId);
        const successors = await expandDagSuccessors(context, item.nodeId);
        if (successors.length === 0) {
            includePathReachedTerminal = true;
            continue;
        }
        for (const successor of successors) {
            const successorState = stateFor(successor.nodeId);
            if (successorState.fromExclude)
                stopPoints.add(successor.nodeId);
        }
        includeFrontier.enqueueMany(factory.createSuccessorItems(item, successors));
    }
    const certificateFailureReason = getCertificateFailureReason();
    if (certificateFailureReason !== undefined) {
        context.telemetry.span.setAttribute("result", "fallback");
        context.telemetry.span.setAttribute("fallback_reason", certificateFailureReason);
        const excluded = await collectAsyncIterableToSet(walkDagReachableNodeIdsCore({
            ...context,
            role: "exclude",
            telemetry: { ...context.telemetry, countYieldedNodes: false },
        }, [excludeNodeId], options));
        context.telemetry.span.incrementCounter("excluded_nodes", excluded.size);
        let removed = 0;
        for (const excludedNodeId of excluded) {
            if (resultCandidates.delete(excludedNodeId))
                removed++;
        }
        context.telemetry.span.incrementCounter("fallback_removed", removed);
    }
    else {
        context.telemetry.span.setAttribute("result", "certified");
    }
    for (const resultCandidate of resultCandidates) {
        recordYieldedNode(context);
        yield resultCandidate;
    }
    function getCertificateFailureReason() {
        if (includePathReachedTerminal || excludePathSplit || stopPoints.size === 0) {
            if (includePathReachedTerminal)
                return "open_include_path";
            if (excludePathSplit)
                return "exclude_path_split";
            return "no_stop_points";
        }
        for (const stopPoint of stopPoints) {
            if (stopPoint !== excludeNodeId && stopPoint !== singleExcludeSuccessor) {
                return "uncertified_stop_point";
            }
        }
        return undefined;
    }
    async function markExcludeSuccessors(excludeReachedNodeId) {
        const successors = await expandDagSuccessors({ ...context, role: "exclude" }, excludeReachedNodeId);
        for (const successor of successors)
            markExcludeReached(successor.nodeId);
        if (hasPathSplit(successors))
            excludePathSplit = true;
    }
}
export async function* walkDagReachableNodeIds(context, nodeIds, options = {}) {
    yield* instrumentAsyncIterable(context.instrumentation, "dag.reachable", (span) => walkDagReachableNodeIdsCore({
        ...context,
        role: "main",
        telemetry: { span, countYieldedNodes: true },
    }, nodeIds, options));
}
async function* walkDagReachableNodeIdsCore(context, nodeIds, options = {}) {
    const role = context.role;
    const visited = new Set();
    const factory = createDagFrontierItemFactory(createBasicDagSchedulingContext);
    const frontier = options.createFrontier?.() ??
        createDefaultTraversalFrontier();
    frontier.enqueueMany(factory.createStartItems(nodeIds, role));
    while (!frontier.isEmpty()) {
        const item = frontier.dequeueOrThrow();
        context.telemetry.span.incrementCounter("traversal_steps");
        if (visited.has(item.nodeId)) {
            context.telemetry.span.incrementCounter("stale_steps");
            continue;
        }
        visited.add(item.nodeId);
        recordYieldedNode(context);
        yield item.nodeId;
        const successors = await expandDagSuccessors(context, item.nodeId);
        const successorItems = factory
            .createSuccessorItems(item, successors)
            .filter((successor) => !visited.has(successor.nodeId));
        frontier.enqueueMany(successorItems);
    }
}
async function expandDagSuccessors(context, nodeId) {
    const role = context.role;
    context.telemetry.span.incrementCounter("successor_expansions");
    context.telemetry.span.incrementCounter(role === "exclude" ? "exclude_expansions" : "main_expansions");
    return await context.graph.getSuccessors(nodeId);
}
function recordYieldedNode(context) {
    if (context.telemetry.countYieldedNodes) {
        context.telemetry.span.incrementCounter("yielded_nodes");
    }
}
/**
 * Selects the context-neutral frontier used when a traversal does not provide one.
 *
 * FIFO with preserved block order is the default because it imposes no
 * domain-specific prioritization.
 */
function createDefaultTraversalFrontier() {
    return createFifoDagFrontier();
}
function createFifoDagFrontier() {
    return new OrderedQueue({
        dequeueOrder: "fifo",
        blockOrder: "preserve",
    });
}
function createDagFrontierItemFactory(createDagSchedulingContext) {
    let discoveredOrder = 0;
    const createStartItem = (nodeId, role) => {
        return createFrontierItem(nodeId, createDagSchedulingContext(role, 0, discoveredOrder++));
    };
    const createStartItems = (nodeIds, role) => {
        return Array.from(nodeIds, (nodeId) => createStartItem(nodeId, role));
    };
    const createSuccessorItems = (parent, successors) => {
        const items = [];
        for (const successor of successors) {
            items.push(createFrontierItem(successor.nodeId, createDagSchedulingContext(parent.scheduling.role, parent.scheduling.depth + 1, discoveredOrder++), successor.domainHint));
        }
        return items;
    };
    return {
        createStartItem,
        createStartItems,
        createSuccessorItems,
    };
}
function createFrontierItem(nodeId, scheduling, domainHint) {
    return {
        nodeId,
        scheduling,
        ...(domainHint === undefined ? {} : { domainHint }),
    };
}
function createBasicDagSchedulingContext(role, depth, discoveredOrder) {
    return {
        role,
        depth,
        discoveredOrder,
    };
}
function hasPathSplit(successors) {
    return successors.length > 1;
}
//# sourceMappingURL=traversal.js.map